import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import PhoneInput from "react-phone-input-2";
import { useSnackbar } from "notistack";
import { Grid, TextField, Button, Box } from "@mui/material";
import { countryMap } from "bundles/common/utils/constants";
import { replaceCurrentRecruiter } from "bundles/Recruiter/constants/recruiterConstants.js";
import sh from "bundles/common/utils/sh";

// Rounded outlined inputs with a green focus/hover border (matches the app design system).
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(153, 68%, 25%)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(153, 68%, 25%)",
  },
  "& label.Mui-focused": {
    color: "hsl(153, 68%, 25%)",
  },
};

const Profile = () => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const intl = useIntl();
  const defaultPhoneValue = "+32 490 12 34 56";
  const initialFormValues = {
    first_name: currentRecruiter.first_name || "",
    last_name: currentRecruiter.last_name || "",
    phone: currentRecruiter.phone || "",
    email: currentRecruiter.email || "",
    job_title: currentRecruiter.job_title || "",
  };

  const [values, setValues] = useState(initialFormValues);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    let required = (
      <FormattedMessage id="form_validation.required"></FormattedMessage>
    );

    if ("first_name" in fieldValues) {
      temp.first_name = fieldValues.first_name ? "" : required;
    }

    if ("last_name" in fieldValues) {
      temp.last_name = fieldValues.last_name ? "" : required;
    }

    if ("email" in fieldValues) {
      temp.email = fieldValues.email ? "" : required;
    }

    if ("job_title" in fieldValues) {
      temp.job_title = fieldValues.job_title ? "" : required;
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const handlePhoneInputClick = () => {
    if (values.phone === defaultPhoneValue)
      setValues({ ...values, ["phone"]: "" });
  };

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
    validate({ [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    if (validate(values)) {
      sh.put("recruiter", {
        authenticity_token: authenticityToken,
        recruiter: { ...values, id: currentRecruiter.id },
      })
        .then(() => {
          replaceCurrentRecruiter(dispatch, authenticityToken, () => {});
          enqueueSnackbar(
            intl.formatMessage({ id: "recruiter.updated_profile" }),
            {
              variant: "success",
            }
          );
        })
        .catch((err) => {
          console.error(err);
          enqueueSnackbar("Oops... Something went wrong", { variant: "error" });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box
        component="h2"
        sx={{
          fontFamily: "Segoe UI Bold, system-ui, sans-serif",
          fontSize: "1.25rem",
          color: "hsl(178, 93%, 10%)",
          letterSpacing: "-0.01em",
          m: 0,
          mb: "24px",
        }}
      >
        <FormattedMessage id="settings.general_info" />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            sx={inputSx}
            label={<FormattedMessage id="devise.signup.first_name" />}
            name="first_name"
            value={values.first_name}
            variant="outlined"
            onBlur={handleInputValue}
            onChange={handleInputValue}
            {...(errors["first_name"] && {
              error: true,
              helperText: errors["first_name"],
            })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            sx={inputSx}
            value={values.last_name}
            type="text"
            name="last_name"
            onBlur={handleInputValue}
            onChange={handleInputValue}
            variant="outlined"
            label={<FormattedMessage id="devise.signup.last_name" />}
            {...(errors["last_name"] && {
              error: true,
              helperText: errors["last_name"],
            })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            sx={inputSx}
            value={values.email}
            type="text"
            name="email"
            onBlur={handleInputValue}
            onChange={handleInputValue}
            variant="outlined"
            label={<FormattedMessage id="devise.signup.email" />}
            {...(errors["email"] && {
              error: true,
              helperText: errors["email"],
            })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <PhoneInput
            country={"be"}
            onlyCountries={countryMap.values}
            inputStyle={{
              height: "56px",
              minWidth: "100%",
              borderRadius: "12px",
              borderColor: "hsl(220, 13%, 82%)",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              color:
                values.phone == defaultPhoneValue
                  ? "hsl(220, 10%, 46%)"
                  : "hsl(220, 25%, 12%)",
            }}
            buttonStyle={{
              borderTopLeftRadius: "12px",
              borderBottomLeftRadius: "12px",
              borderColor: "hsl(220, 13%, 82%)",
            }}
            value={values.phone}
            color="primary"
            placeholder="+32 490 12 34 56"
            inputProps={{
              name: "phone",
              autoFocus: false,
              value: values.phone,
              onBlur: handleInputValue,
              onChange: handleInputValue,
              onClick: handlePhoneInputClick,
            }}
            {...(errors["phone"] && {
              error: true,
              helperText: errors["phone"],
            })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            sx={inputSx}
            value={values.job_title}
            type="text"
            name="job_title"
            onBlur={handleInputValue}
            onChange={handleInputValue}
            variant="outlined"
            label={<FormattedMessage id="recruiter.job_title" />}
            {...(errors["job_title"] && {
              error: true,
              helperText: errors["job_title"],
            })}
          />
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Button
            disabled={loading}
            type="submit"
            variant="rounded"
            color="secondaryContained"
            style={{
              width: "200px",
              marginTop: "16px",
              textTransform: "none",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            }}
          >
            <FormattedMessage id="save" />
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default Profile;
